// src/components/pages/Landing.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "../layout/AppShell";
import Button from "../ui/Button";
import Card from "../ui/Card";
import Ball, { BallsRow } from "../ui/Ball";
import Countdown, { NEXT_MEGA_MILLIONS, NEXT_POWERBALL } from "../ui/Countdown";
import Icon from "../ui/Icon";
import ScratchComingSoonModal from "../scratch/ScratchComingSoonModal";

import axiosInstance from "../../utils/AxiosInstance";
import { getNextDraws, formatJackpot } from "../../services/resultsService";
import { useScratchFeature } from "../../contexts/ScratchFeatureContext";

/**
 * Defensive parser for the /site/match-result/total-by-lottery/:lottery
 * endpoint — backend may return the number directly, an array, or a
 * nested object. Mirrors the original Counters.jsx logic.
 */
function extractCounterValue(data) {
  if (data === null || data === undefined) return null;
  if (typeof data === "number" && Number.isFinite(data)) return data;
  if (typeof data === "string") {
    const n = Number(data.replace(/[, $]/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  if (Array.isArray(data) && data.length > 0) return extractCounterValue(data[0]);
  if (typeof data === "object") {
    for (const key of ["value", "amount", "total", "totalAmount", "totalValue", "matchTotal", "sum"]) {
      if (data[key] !== undefined) {
        const v = extractCounterValue(data[key]);
        if (v != null) return v;
      }
    }
  }
  return null;
}

const formatBigNumber = (n) => {
  if (n === null || n === undefined) return "—";
  return `US$${Number(n).toLocaleString("en-US")}`;
};

export default function Landing() {
  const navigate = useNavigate();
  const { scratchEnabled, loading: scratchFeatureLoading } = useScratchFeature();

  // "Coming soon" teaser — only relevant while the Scratch-Off feature is
  // off; once it's on, the real marketing section further down replaces it.
  const [showPromo, setShowPromo] = useState(true);

  const nextMM = useMemo(() => NEXT_MEGA_MILLIONS(), []);
  const nextPB = useMemo(() => NEXT_POWERBALL(), []);

  // Authoritative next-draw date + jackpot from the backend (public
  // endpoint). Falls back to the client-side estimates / static values
  // below if the request fails or hasn't resolved yet.
  const [nextDraws, setNextDraws] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await getNextDraws();
      if (!cancelled) setNextDraws(data);
    })();
    return () => { cancelled = true; };
  }, []);

  const [stats, setStats] = useState({ POWER_BALL: null, MEGA_MILLIONS: null });
  const [statsLoading, setStatsLoading] = useState(true);

  // Auth detection so the hero CTAs route correctly. null = unknown
  // (initial check in flight), true = logged in, false = not logged in.
  // Buttons treat unknown the same as not-logged-in to avoid flashing the
  // "logged in" state to a stranger if /logged-user is slow.
  const [isAuthed, setIsAuthed] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get("/site/user/logged-user");
        if (!cancelled) setIsAuthed(Boolean(res?.data?.id));
      } catch {
        if (!cancelled) setIsAuthed(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Route logged-in users to the app, strangers to sign-up.
  const goGenerate = () => navigate(isAuthed ? "/generator" : "/sign-up");
  const goScratch  = () => navigate(isAuthed ? "/scratch"   : "/sign-up");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pb, mm] = await Promise.all([
          axiosInstance.get("/site/match-result/total-by-lottery/POWER_BALL"),
          axiosInstance.get("/site/match-result/total-by-lottery/MEGA_MILLIONS"),
        ]);
        if (cancelled) return;
        setStats({
          POWER_BALL:    extractCounterValue(pb?.data),
          MEGA_MILLIONS: extractCounterValue(mm?.data),
        });
      } catch {
        // Endpoint may not be implemented in dev — fall through to "—"
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <AppShell>
      {!scratchFeatureLoading && !scratchEnabled && showPromo && (
        <ScratchComingSoonModal onClose={() => setShowPromo(false)} />
      )}

      <div className="px-4 sm:px-6">

        {/* ============================================================
            Hero
            ============================================================ */}
        <section className="relative text-center pt-12 sm:pt-20 pb-10">
          <div
            className="absolute left-1/2 -translate-x-1/2 -top-10 w-[420px] h-[420px] rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(46,229,107,0.16), transparent 60%)",
              filter: "blur(40px)",
            }}
            aria-hidden="true"
          />

          {/* High-contrast "pill" badge — solid nile-blue bg, white text */}
          <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full mb-5"
               style={{
                 background: "var(--panel)",
                 color: "var(--on-panel)",
                 fontSize: 12,
                 fontWeight: 500,
                 letterSpacing: 0.2,
               }}>
            <Icon name="star-filled" size={12} style={{ color: "var(--gold)" }} />
            Data-driven lottery picks
          </div>

          <h1
            className="font-semibold leading-[1.05] tracking-tight"
            style={{
              fontSize: "clamp(2.5rem, 6vw, 4rem)",
              color: "var(--txt)",
            }}
          >
            Stop <span style={{ color: "var(--gold)" }}>guessing</span>
            <br />
            your numbers.
          </h1>

          <p
            className="mx-auto mt-5 text-[15px] leading-relaxed"
            style={{ color: "var(--txt-2)", maxWidth: 520 }}
          >
            Quantum Lotto uses a model trained on 20+ years of real Mega Millions
            and Powerball draws — retrained automatically before every drawing
            so your picks always reflect the latest data.
          </p>

          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button size="lg" onClick={goGenerate}>
              <Icon name="sparkles" size={16} />
              Generate my numbers
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => {
                const el = document.getElementById("how");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              How it works
            </Button>
          </div>

          <div
            className="mt-7 flex flex-wrap justify-center gap-5 text-[12px]"
            style={{ color: "var(--txt-2)" }}
          >
            <Stat icon="shield-check" label="SSL secured" />
            <Stat icon="ticket"       label="Mega Millions, Powerball & Scratch-Offs" />
            <Stat icon="brain"        label="Retrained every draw" />
          </div>
        </section>

        {/* ============================================================
            Total winnings panel — dark navy, restored from original site
            ============================================================ */}
        <section className="my-10">
          <Card variant="panel" className="relative overflow-hidden">
            <DotPattern />
            <div className="relative z-10 text-center pb-16 pt-4">
              <h2
                className="font-semibold leading-tight"
                style={{
                  fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
                  color: "var(--on-panel)",
                }}
              >
                Total winnings tracked from
                <br />
                <span style={{ color: "var(--gold)" }}>Quantum Lotto combinations</span>
              </h2>
              <p
                className="mt-3 text-[13px]"
                style={{ color: "var(--on-panel-2)", maxWidth: 520, margin: "12px auto 0" }}
              >
                Cumulative prize amounts (US$) our community has won across
                both lotteries since our launch on July 1, 2026.
              </p>
            </div>

            {/* Counter cards — overlap the panel bottom edge */}
            <div className="relative z-10 grid grid-cols-2 gap-3 sm:gap-4 max-w-[600px] mx-auto -mb-20">
              <CounterCard
                label="Power Ball"
                value={statsLoading ? "…" : formatBigNumber(stats.POWER_BALL)}
              />
              <CounterCard
                label="Mega Millions"
                value={statsLoading ? "…" : formatBigNumber(stats.MEGA_MILLIONS)}
              />
            </div>
          </Card>

          {/* Spacer for the overlapping counters */}
          <div className="h-20" />
        </section>

        {/* ============================================================
            Countdown strip
            ============================================================ */}
        <section className="grid gap-3 md:grid-cols-2 mb-12">
          <CountdownCard
            name="Mega Millions"
            target={nextDraws.MEGA_MILLIONS?.target || nextMM}
            jackpot={nextDraws.MEGA_MILLIONS?.value != null ? formatJackpot(nextDraws.MEGA_MILLIONS.value) : "$340M"}
            onCta={goGenerate}
          />
          <CountdownCard
            name="Powerball"
            target={nextDraws.POWER_BALL?.target || nextPB}
            jackpot={nextDraws.POWER_BALL?.value != null ? formatJackpot(nextDraws.POWER_BALL.value) : "$120M"}
            onCta={goGenerate}
          />
        </section>

        {/* ============================================================
            How it works
            ============================================================ */}
        <section id="how" className="mb-14 scroll-mt-20">
          <div className="text-center mb-7">
            <div className="lg-label mb-3">How it works</div>
            <h2 className="font-semibold" style={{ fontSize: "clamp(1.75rem, 4vw, 2.25rem)" }}>
              Real data. Real training. <span style={{ color: "var(--gold)" }}>Real picks.</span>
            </h2>
          </div>

          <div className="grid gap-3 md:grid-cols-4">
            <Feature
              icon="database"
              title="20+ years of history"
              body="Every Mega Millions and Powerball draw, ingested and indexed for analysis."
            />
            <Feature
              icon="brain"
              title="Trained model"
              body="A model trained on real-world results — no superstition, no quick math."
            />
            <Feature
              icon="refresh"
              title="Retrained every draw"
              body="Patterns shift. So does our model. Your picks always reflect the latest data."
            />
            <Feature
              icon="ticket"
              title="Your pick, ready"
              body="Copy your numbers and play at any licensed retailer or lottery app."
            />
          </div>

          {/* Sample pick card */}
          <Card variant="gold" className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="lg-label">Sample pick · Mega Millions</div>
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium"
                style={{
                  background: "var(--gold-dim)",
                  color: "var(--gold)",
                  border: "1px solid var(--gold-border)",
                }}
              >
                Live
              </span>
            </div>
            <BallsRow>
              <Ball value={7} />
              <Ball value={23} />
              <Ball value={31} />
              <Ball value={44} />
              <Ball value={60} />
              <Ball value={11} variant="gold" />
            </BallsRow>
            <p className="text-[12px] mt-3" style={{ color: "var(--txt-2)" }}>
              Green ball = Mega Ball. Generated with our trained model.
            </p>
          </Card>
        </section>

        {/* ============================================================
            Scratch-Off Intelligence — new module
            ============================================================ */}
        {scratchEnabled && (
          <section id="scratch" className="mb-14 scroll-mt-20">
            <div className="text-center mb-7">
              <div className="lg-label mb-3">Also new</div>
              <h2 className="font-semibold" style={{ fontSize: "clamp(1.75rem, 4vw, 2.25rem)" }}>
                Smarter <span style={{ color: "var(--gold)" }}>scratch-off</span> picks.
              </h2>
              <p
                className="mx-auto mt-3 text-[14px] leading-relaxed"
                style={{ color: "var(--txt-2)", maxWidth: 560 }}
              >
                We pull public prize-inventory data from state lotteries every day
                and rank each game by how much of its remaining prize pool is still
                up for grabs. No prediction of winners — just better visibility
                into relative value.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-3 mb-6">
              <Feature
                icon="database"
                title="Public prize data"
                body="Sourced directly from each state lottery's published inventory — TX, FL, NY in the launch lineup."
              />
              <Feature
                icon="trophy"
                title="Scratch Score 0–100"
                body="A relative-value score that blends top-prize ratio, expected value, mid-tier strength, and freshness."
              />
              <Feature
                icon="bell"
                title="Optional alerts"
                body="Get notified when a top prize is claimed, when a game's score moves, or when a new game launches."
              />
            </div>

            <Card variant="soft">
              <div className="flex items-center gap-4">
                <div
                  className="shrink-0 w-16 h-16 rounded-2xl flex flex-col items-center justify-center"
                  style={{
                    background: "var(--gold)",
                    color: "var(--ink-on)",
                  }}
                >
                  <span className="font-bold leading-none" style={{ fontSize: 22 }}>87</span>
                  <span className="text-[9px] uppercase tracking-wider mt-1">Score</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[14px]" style={{ color: "var(--txt)" }}>
                    Sample — TX $50 Million Cash Blast
                  </div>
                  <div className="text-[12px] mt-1" style={{ color: "var(--txt-2)" }}>
                    $20 ticket · 4 of 6 top prizes remain · better relative value than 91% of TX games today
                  </div>
                  <div className="text-[11px] mt-2" style={{ color: "var(--txt-3)" }}>
                    Based on public prize data. No guarantee of winning.
                  </div>
                </div>
              </div>
            </Card>

            <div className="mt-6 text-center">
              <Button size="lg" onClick={goScratch}>
                <Icon name="ticket" size={16} />
                Explore Scratch-Off Intelligence
              </Button>
            </div>
          </section>
        )}

        {/* ============================================================
            Pricing snapshot
            ============================================================ */}
        <section id="plans" className="mb-14 scroll-mt-20">
          <div className="text-center mb-7">
            <div className="lg-label mb-3">Plans</div>
            <h2 className="font-semibold" style={{ fontSize: "clamp(1.75rem, 4vw, 2.25rem)" }}>
              Pay-as-you-play or go unlimited.
            </h2>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <PlanPreview
              title="Single"
              price="$1"
              tagline="1 credit · 5 games"
              perks={["Both lotteries", "Never expires"]}
              onClick={() => navigate("/plans")}
            />
            <PlanPreview
              title="Gold"
              price="$20"
              tagline="50 credits · 250 games"
              perks={["Both lotteries", "Prediction model v2", "Ticket print assistant"]}
              onClick={() => navigate("/plans")}
            />
            <PlanPreview
              title="Monthly"
              price="$5"
              tagline="30 credits / month"
              perks={["Auto-renew", "Credit roll-over", "Cancel anytime"]}
              featured
              onClick={() => navigate("/plans")}
            />
          </div>

          <div className="mt-6 text-center">
            <Button size="lg" onClick={() => navigate("/sign-up")}>
              Start playing smarter
              <Icon name="arrow-right" size={16} />
            </Button>
            <p className="mt-3 text-[11px]" style={{ color: "var(--txt-3)" }}>
              For entertainment purposes only · Must be 18+ in an eligible US state
            </p>
          </div>
        </section>

        {/* ============================================================
            FAQ
            ============================================================ */}
        <section id="faq" className="mb-14 scroll-mt-20">
          <div className="text-center mb-7">
            <div className="lg-label mb-3">FAQ</div>
            <h2 className="font-semibold" style={{ fontSize: "clamp(1.75rem, 4vw, 2.25rem)" }}>
              Frequently asked.
            </h2>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <Faq
              q="Does Quantum Lotto guarantee I'll win?"
              a="No, and anyone who claims otherwise isn't being honest. The lottery is a game of chance. Our model surfaces patterns from historical data — it improves the quality of your picks, but it cannot change the odds of the draw."
            />
            <Faq
              q="How is the model trained?"
              a="On 20+ years of real Mega Millions and Powerball results plus other public, draw-related signals. It's retrained before every draw so your picks always reflect the latest data."
            />
            <Faq
              q="Can I use the same pick on a real ticket?"
              a="Yes. Once your numbers are generated, copy them and play them at any licensed retailer or via your state's lottery app. We don't sell tickets ourselves."
            />
            <Faq
              q="What's a credit?"
              a="One credit generates one set of 5 plays for the lottery you select. You can buy credits one-time or get a monthly bundle."
            />
            {scratchEnabled && (
              <>
                <Faq
                  q="Does the Scratch Score predict winners?"
                  a="No. Scratch-offs are random. The Scratch Score ranks games by the relative health of their remaining prize pool — based on each state lottery's published prize-inventory data. Higher score means better relative value today, not a higher chance of winning."
                />
                <Faq
                  q="Which states does Scratch-Off Intelligence cover?"
                  a="Texas, Florida, and New York at launch. We're adding California, Pennsylvania, Georgia, and Michigan next — each one comes online as soon as we've validated the parser against the state's published data."
                />
              </>
            )}
          </div>
        </section>

        {/* ============================================================
            Contact CTA (anchor #contact)
            ============================================================ */}
        <section id="contact" className="mb-10 scroll-mt-20">
          <Card variant="panel" className="relative overflow-hidden text-center">
            <DotPattern />
            <div className="relative z-10 py-4">
              <h2
                className="font-semibold"
                style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", color: "var(--on-panel)" }}
              >
                Questions? <span style={{ color: "var(--gold)" }}>Let's chat.</span>
              </h2>
              <p
                className="mt-3 text-[13px]"
                style={{ color: "var(--on-panel-2)", maxWidth: 480, margin: "12px auto 24px" }}
              >
                Reach out and we'll get back to you within one business day.
              </p>
              <Button variant="panel" onClick={() => navigate("/contact")}>
                <Icon name="send" size={14} />
                Send a message
              </Button>
            </div>
          </Card>
        </section>
      </div>
    </AppShell>
  );
}

/* ---------------- helpers ---------------- */

function Stat({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon name={icon} size={14} style={{ color: "var(--gold)" }} />
      {label}
    </span>
  );
}

function Feature({ icon, title, body }) {
  return (
    <Card variant="soft">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
        style={{
          background: "var(--ink-dim)",
          border: "1px solid var(--ink-border)",
          color: "var(--ink)",
        }}
      >
        <Icon name={icon} size={18} />
      </div>
      <div className="font-medium text-[14px] mb-1.5" style={{ color: "var(--txt)" }}>
        {title}
      </div>
      <p className="text-[12px] leading-relaxed" style={{ color: "var(--txt-2)" }}>
        {body}
      </p>
    </Card>
  );
}

function CountdownCard({ name, target, jackpot, onCta }) {
  return (
    <Card>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="lg-label mb-2">Next {name}</div>
          <Countdown target={target} />
        </div>
        <div className="text-right">
          <div className="lg-label mb-1">Est. jackpot</div>
          <div className="font-semibold" style={{ fontSize: 26, color: "var(--ink)" }}>
            {jackpot}
          </div>
        </div>
      </div>
      {/*
        CTA: green background, nile-blue text. We render this as a plain
        button (not <Button variant="outline">) so the green/blue pairing
        renders consistently in both themes — green is fixed across
        themes, and --ink-on stays nile-blue in both themes (it's the
        readable text color when sitting on the green chip).
      */}
      <button
        type="button"
        onClick={onCta}
        className="w-full rounded-full px-4 py-2 text-[13px] font-semibold flex items-center justify-between transition-colors"
        style={{
          background: "var(--gold)",
          color: "var(--ink-on)",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "var(--gold-lt)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "var(--gold)"; }}
      >
        See tonight's prediction
        <Icon name="arrow-right" size={14} />
      </button>
    </Card>
  );
}

function CounterCard({ label, value }) {
  // The card sits on the dark navy panel. To keep the lottery name
  // legible inside that dark area we render it FIRST (as a white-on-dark
  // label), then the icon and big number on the lighter card surface
  // below. The whole card is white/surface so the number stays readable.
  return (
    <Card className="text-center !p-5 !pt-9 relative">
      {/* Top accent line */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px] rounded-t-[var(--r-xl)]"
        style={{
          background: "linear-gradient(90deg, transparent, var(--gold), transparent)",
        }}
      />

      {/* Lottery name — large, prominent, the FIRST thing in the card */}
      <div
        className="absolute -top-7 left-0 right-0 text-center"
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--on-panel)",
        }}
      >
        {label}
      </div>

      <div
        className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-3"
        style={{ background: "var(--gold)", color: "#23364C" }}
      >
        <Icon name="arrow-right" size={18} style={{ transform: "rotate(-90deg)" }} />
      </div>
      <div className="font-bold" style={{ fontSize: 24, color: "var(--txt)" }}>
        {value}
      </div>
      {/* Repeat label inside the card too so the association is unambiguous */}
      <div className="text-[12px] mt-1" style={{ color: "var(--txt-2)" }}>
        {label}
      </div>
    </Card>
  );
}

function PlanPreview({ title, price, tagline, perks, featured, onClick }) {
  return (
    <Card
      variant={featured ? "gold" : "soft"}
      className="relative cursor-pointer hover:-translate-y-[1px] transition-transform flex flex-col h-full"
    >
      {featured && (
        <span
          className="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide"
          style={{ background: "var(--gold)", color: "#0E2A18" }}
        >
          Most popular
        </span>
      )}
      <div className="flex items-baseline justify-between mb-1">
        <div className="font-medium" style={{ color: featured ? "var(--gold)" : "var(--txt)" }}>
          {title}
        </div>
        <div className="font-semibold" style={{ fontSize: 22, color: featured ? "var(--gold)" : "var(--txt)" }}>
          {price}
        </div>
      </div>
      <p className="text-[12px] mb-3" style={{ color: "var(--txt-2)" }}>
        {tagline}
      </p>
      {/* flex-1 lets the perks block absorb any extra vertical space so the
          CTA button below sits flush with the bottom of the card on every
          plan, regardless of how many perks each one lists. */}
      <ul className="space-y-1.5 mb-4 flex-1">
        {perks.map((p) => (
          <li key={p} className="flex items-center gap-2 text-[12px]" style={{ color: "var(--txt-2)" }}>
            <Icon name="check" size={14} style={{ color: "var(--teal)" }} />
            {p}
          </li>
        ))}
      </ul>
      <Button variant={featured ? "gold" : "outline"} size="sm" full onClick={onClick}>
        Choose {title}
      </Button>
    </Card>
  );
}

function Faq({ q, a }) {
  return (
    <Card variant="soft">
      <div className="flex items-start gap-2.5 mb-2">
        <Icon name="help-circle" size={18} style={{ color: "var(--gold)", marginTop: 1, flexShrink: 0 }} />
        <div className="font-medium text-[14px]" style={{ color: "var(--txt)" }}>
          {q}
        </div>
      </div>
      <p className="text-[13px] leading-relaxed pl-7" style={{ color: "var(--txt-2)" }}>
        {a}
      </p>
    </Card>
  );
}

/* Decorative dot pattern for dark panels — pure CSS, no asset */
function DotPattern() {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(circle, rgba(255,255,255,0.10) 1px, transparent 1.4px)",
        backgroundSize: "18px 18px",
        maskImage: "radial-gradient(ellipse at center, black 50%, transparent 85%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 50%, transparent 85%)",
      }}
    />
  );
}